import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import RoutePagesSection from "@/components/RoutePagesSection";
import { Shield, Users, Award, Clock, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AboutPage() {
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/cms/site-settings`);
        setSiteSettings(response.data.settings);
      } catch (error) {
        console.error("Failed to fetch settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const contact = siteSettings?.contact || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">About Roman Cars</h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">Your trusted transportation partner providing premium taxi services across the United Kingdom</p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-gray-600 mb-4 text-lg">
                Roman Cars was founded with a simple mission: to provide reliable, comfortable, and affordable transportation services to our community.
              </p>
              <p className="text-gray-600 mb-4">
                Over the years, we have grown from a small local taxi service to a trusted transportation partner serving customers across the UK. Our commitment to excellence and customer satisfaction has remained unchanged.
              </p>
              <p className="text-gray-600">
                Today, we continue to invest in modern vehicles, professional driver training, and cutting-edge booking technology to ensure every journey with Roman Cars exceeds expectations.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-orange-50 p-6 rounded-xl text-center">
                <div className="text-4xl font-bold text-orange-500 mb-2">10+</div>
                <div className="text-gray-600">Years Experience</div>
              </div>
              <div className="bg-orange-50 p-6 rounded-xl text-center">
                <div className="text-4xl font-bold text-orange-500 mb-2">50K+</div>
                <div className="text-gray-600">Happy Customers</div>
              </div>
              <div className="bg-orange-50 p-6 rounded-xl text-center">
                <div className="text-4xl font-bold text-orange-500 mb-2">100+</div>
                <div className="text-gray-600">Professional Drivers</div>
              </div>
              <div className="bg-orange-50 p-6 rounded-xl text-center">
                <div className="text-4xl font-bold text-orange-500 mb-2">24/7</div>
                <div className="text-gray-600">Service Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl text-center">
              <Shield className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Safety First</h3>
              <p className="text-gray-600">Your safety is our top priority on every journey</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <Users className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Customer Focus</h3>
              <p className="text-gray-600">We put our customers at the heart of everything</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <Award className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Excellence</h3>
              <p className="text-gray-600">We strive for excellence in every ride</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <Clock className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Reliability</h3>
              <p className="text-gray-600">Always on time, every time</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Experience the Difference?</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book" className="px-8 py-4 bg-white text-orange-500 rounded-xl font-semibold hover:bg-gray-100">Book Now</Link>
            {contact.phone && (<a href={`tel:${contact.phone}`} className="px-8 py-4 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 flex items-center gap-2"><Phone size={20} />{contact.phone}</a>)}
          </div>
        </div>
      </section>

      {/* Route Pages */}
      <RoutePagesSection />

      <Footer />
    </div>
  );
}
